import { useEffect, useState, type ChangeEvent, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
  documentTemplatesApi,
  type DocumentTemplate,
  type DocumentTemplateFields,
  type DocumentTemplateKind,
} from "../../api/documentTemplates";
import type { ApiError } from "../../api/client";
import { useSectionAccess } from "../../auth/useSectionAccess";
import { useToast } from "../../components/Toast";
import { formatDate } from "../../lib/format";

const KINDS: DocumentTemplateKind[] = ["SALES_CONTRACT", "PURCHASE_CONTRACT", "GOODS_DOCUMENT"];

const KIND_TITLE: Record<DocumentTemplateKind, string> = {
  SALES_CONTRACT: "Sales contracts with customers",
  PURCHASE_CONTRACT: "Purchase contracts (soy, wheat, maize…)",
  GOODS_DOCUMENT: "Documents that go with the goods",
};

const KIND_USE: Record<DocumentTemplateKind, string> = {
  SALES_CONTRACT: "Picked on the contract form, or with “Make from template” on a contract; the Word becomes a version of the contract.",
  PURCHASE_CONTRACT: "Kept here; making a purchase contract from a template comes with the purchase contracts.",
  GOODS_DOCUMENT: "Kept here; each customer’s set of documents and making them for a dispatch order come next.",
};

/** Starts a download from a presigned address without leaving the page. */
function download(url: string) {
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  a.click();
}

/**
 * The gallery of Word templates contracts and documents are made from, in three groups. The directors add a .docx to
 * a group, replace its file, or take it out of use; a template out of use stays listed, since a version made from it
 * still names it. Below, the fields a template can carry, as the server fills them.
 */
export default function AdminDocumentTemplatesPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { canWrite } = useSectionAccess("document_templates");
  const [templates, setTemplates] = useState<DocumentTemplate[] | null>(null);
  const [fields, setFields] = useState<DocumentTemplateFields[]>([]);
  const [failed, setFailed] = useState<string | null>(null);
  const [adding, setAdding] = useState<DocumentTemplateKind | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    try {
      setTemplates(await documentTemplatesApi.list());
      setFailed(null);
    } catch (e) {
      setFailed((e as ApiError).message);
    }
  }

  useEffect(() => {
    void load();
    documentTemplatesApi.fields().then(setFields).catch(() => undefined);
  }, []);

  async function act(id: string, work: () => Promise<unknown>, done: string) {
    setBusy(id);
    try {
      await work();
      toast.success(done);
      await load();
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(null);
    }
  }

  async function downloadTemplate(tpl: DocumentTemplate) {
    try {
      download((await documentTemplatesApi.downloadUrl(tpl.id)).url);
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  const sales = fields.find((f) => f.kind === "SALES_CONTRACT");

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("documentTemplates.title", "Document templates")}</h1>
          <div className="sub" style={{ maxWidth: "80ch" }}>
            {t("documentTemplates.subtitle", {
              example: "{{kupac}}",
              defaultValue: "Word templates the portal fills with data to make contracts and documents. A field is written in double braces, e.g. {{example}}; a field the portal does not know stays as written.",
            })}
          </div>
        </div>
      </div>

      {failed && <div className="empty">{failed}</div>}

      {KINDS.map((kind) => {
        const rows = (templates ?? []).filter((x) => x.kind === kind);
        return (
          <section key={kind} className="card">
            <div className="card-h">
              <div>
                <h3>{t(`documentTemplates.kind.${kind}`, KIND_TITLE[kind])}</h3>
                <div className="sub">{t(`documentTemplates.use.${kind}`, KIND_USE[kind])}</div>
              </div>
              {canWrite && adding !== kind && (
                <button type="button" className="btn primary sm" onClick={() => setAdding(kind)}>
                  {t("documentTemplates.add", "Add template")}
                </button>
              )}
            </div>
            {adding === kind && (
              <NewTemplate kind={kind} onClose={() => setAdding(null)}
                           onSaved={async () => { setAdding(null); await load(); }} />
            )}
            <div className="card-b tight">
              {templates === null ? (
                <p style={note}>{t("common.loading", "Loading…")}</p>
              ) : rows.length === 0 ? (
                <p style={note}>{t("documentTemplates.empty", "No templates in this group yet.")}</p>
              ) : (
                <table className="tbl kartice" style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th>{t("documentTemplates.col.name", "Template")}</th>
                      <th>{t("documentTemplates.col.file", "File")}</th>
                      <th>{t("documentTemplates.col.updated", "Changed")}</th>
                      <th>{t("documentTemplates.col.state", "State")}</th>
                      <th style={{ textAlign: "right" }}>{t("common.actions", "Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((x) => (
                      <tr key={x.id} style={x.active ? undefined : { opacity: 0.6 }}>
                        <td className="card-naslov" data-label={t("documentTemplates.col.name", "Template")}>
                          <b>{x.name}</b>
                          {x.description && <div style={{ color: "var(--ink-3)", fontSize: 12 }}>{x.description}</div>}
                        </td>
                        <td data-label={t("documentTemplates.col.file", "File")}>
                          {/* One piece, so a phone's label-value row keeps the size with the name. */}
                          <span>
                            {x.filename} <span style={{ color: "var(--ink-3)" }}>· {Math.max(1, Math.round(x.sizeBytes / 1024))} KB</span>
                          </span>
                        </td>
                        <td data-label={t("documentTemplates.col.updated", "Changed")}>{formatDate(x.updatedAt)}</td>
                        <td data-label={t("documentTemplates.col.state", "State")}>
                          {x.active ? t("documentTemplates.active", "In use") : t("documentTemplates.inactive", "Out of use")}
                        </td>
                        <td className="card-actions" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                            <button type="button" className="btn sm" onClick={() => downloadTemplate(x)}>
                              {t("documentTemplates.download", "Download")}
                            </button>
                            {canWrite && (
                              <label className="btn sm" aria-disabled={busy === x.id}>
                                {t("documentTemplates.replace", "Replace file")}
                                <input type="file" accept=".docx" style={{ display: "none" }} disabled={busy === x.id}
                                       onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                         const f = e.target.files?.[0];
                                         e.target.value = "";
                                         if (f) void act(x.id, () => documentTemplatesApi.replaceFile(x.id, f),
                                           t("documentTemplates.replaced", "File replaced."));
                                       }} />
                              </label>
                            )}
                            {canWrite && (
                              <button type="button" className="btn sm ghost" disabled={busy === x.id}
                                      onClick={() => void act(x.id, () => documentTemplatesApi.update(x.id, { active: !x.active }),
                                        x.active ? t("documentTemplates.deactivated", "Taken out of use.")
                                                 : t("documentTemplates.activated", "Back in use."))}>
                                {x.active ? t("documentTemplates.deactivate", "Take out of use")
                                          : t("documentTemplates.activate", "Put back in use")}
                              </button>
                            )}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        );
      })}

      {sales && (
        <section className="card">
          <div className="card-h">
            <div>
              <h3>{t("documentTemplates.fields.title", "Fields in a sales contract template")}</h3>
              <div className="sub">
                {t("documentTemplates.fields.lineHint", {
                  example: "{{stavka.naziv}}",
                  defaultValue: "A table row with a field like {{example}} is repeated once per contract line.",
                })}
              </div>
            </div>
          </div>
          <div className="card-b" style={{ display: "grid", gap: 18, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
            <FieldList title={t("documentTemplates.fields.document", "Contract")} prefix=""
                       names={sales.document} label={(n) => t(`documentTemplates.field.${n}`, n)} />
            <FieldList title={t("documentTemplates.fields.line", "Line")} prefix="stavka."
                       names={sales.line} label={(n) => t(`documentTemplates.lineField.${n}`, n)} />
          </div>
        </section>
      )}
    </div>
  );
}

function FieldList({ title, prefix, names, label }: {
  title: string; prefix: string; names: string[]; label: (name: string) => string;
}) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".4px", marginBottom: 8 }}>
        {title}
      </div>
      <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "max-content 1fr", gap: "6px 14px", fontSize: 13 }}>
        {names.map((n) => (
          <div key={n} style={{ display: "contents" }}>
            <dt><code style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{`{{${prefix}${n}}}`}</code></dt>
            <dd style={{ margin: 0, color: "var(--ink-2)" }}>{label(n)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** Adding a template to a group: its name, a word on what it is for, and the .docx. */
function NewTemplate({ kind, onClose, onSaved }: {
  kind: DocumentTemplateKind; onClose: () => void; onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!file) return;
    setBusy(true);
    try {
      await documentTemplatesApi.create(kind, name.trim(), description, file);
      toast.success(t("documentTemplates.saved", "Template saved."));
      await onSaved();
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ padding: 18, borderBottom: "1px solid var(--border)", display: "grid", gap: 12,
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", alignItems: "end" }}>
      <label style={fieldLabel}>
        <span>{t("documentTemplates.form.name", "Name")}</span>
        <input className="input" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
      </label>
      <label style={fieldLabel}>
        <span>{t("documentTemplates.form.description", "Description")}</span>
        <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <div style={fieldLabel}>
        <span>{t("documentTemplates.form.file", "Word file (.docx)")}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <label className="btn sm">
            {t("documentTemplates.form.pick", "Choose file")}
            <input type="file" accept=".docx" style={{ display: "none" }}
                   onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </label>
          <span style={{ color: file ? "var(--ink-2)" : "var(--ink-4)", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {file ? file.name : t("documentTemplates.form.none", "No file chosen")}
          </span>
        </span>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" className="btn sm" onClick={onClose}>{t("documentTemplates.form.cancel", "Cancel")}</button>
        <button type="button" className="btn primary sm" disabled={busy || !file || name.trim() === ""} onClick={() => void save()}>
          {t("documentTemplates.form.save", "Save")}
        </button>
      </div>
    </div>
  );
}

const note: CSSProperties = { margin: 0, padding: 18, color: "var(--ink-3)", fontSize: 13 };
const fieldLabel: CSSProperties = { display: "flex", flexDirection: "column", gap: 6, fontSize: 12, color: "var(--ink-3)" };
